import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Sparkles, X } from 'lucide-react';
import { antiBrushHeaders } from '../utils/analytics';
import './AppendChaptersButton.css';

const apiBaseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:65181'}${process.env.REACT_APP_API_VERSION === undefined ? '/api/v1' : process.env.REACT_APP_API_VERSION}`;

interface AppendResult {
  outlines?: unknown[];
  chapters?: unknown[];
  hooks?: unknown[];
  total_chapters?: number;
}

interface Props {
  projectId?: number | null;
  inspirationId?: number | null;
  currentCount: number;
  maxChapters: number;
  disabled?: boolean;
  compact?: boolean;
  onComplete: (result: AppendResult) => Promise<void> | void;
  onMessage?: (message: string) => void;
}

const responseError = async (response: Response) => {
  try {
    const data = await response.json();
    return String(data?.detail || data?.message || '追加章节失败');
  } catch {
    return '追加章节失败，请稍后重试';
  }
};

const AppendChaptersButton: React.FC<Props> = ({
  projectId, inspirationId, currentCount, maxChapters, disabled, compact, onComplete, onMessage,
}) => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(8);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [error, setError] = useState('');
  const available = Math.max(0, Number(maxChapters || 0) - currentCount);
  const maxAppend = Math.min(100, available);
  const requestedCount = Number(count);
  const countValid = Number.isInteger(requestedCount) && requestedCount >= 1 && requestedCount <= maxAppend;
  const safeCount = useMemo(() => countValid ? requestedCount : Math.max(1, Math.min(requestedCount || 1, Math.max(1, maxAppend))), [countValid, maxAppend, requestedCount]);

  useEffect(() => {
    if (open) setCount(Math.min(8, Math.max(1, maxAppend)));
  }, [maxAppend, open]);

  const append = async () => {
    if (!projectId || !inspirationId || maxAppend < 1) return;
    setBusy(true);
    setError('');
    setProgress('AI正在理解已有剧情、人物状态和伏笔...');
    try {
      const token = localStorage.getItem('lingbi-token') || '';
      const response = await fetch(`${apiBaseUrl}/novel_project_app/projects/${projectId}/chapters/append-stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...antiBrushHeaders(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ inspiration_id: inspirationId, add_count: safeCount }),
      });
      if (!response.ok || !response.body) throw new Error(await responseError(response));
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let result: AppendResult | null = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const event = JSON.parse(line);
          if (event.type === 'chunk') setProgress('AI正在规划后续剧情和章节衔接...');
          if (event.type === 'field' && event.field === 'chapter') {
            setProgress(`正在写入第 ${Number(event.index || 0) + 1} 章计划...`);
          }
          if (event.type === 'error') throw new Error(event.message || '追加章节失败');
          if (event.type === 'done') result = event;
        }
      }
      if (!result) throw new Error('AI追加章节结果为空');
      setProgress('章节追加完成，正在同步工作台...');
      await onComplete(result);
      onMessage?.(`已智能追加 ${safeCount} 章，当前共 ${result.total_chapters || currentCount + safeCount} 章`);
      setOpen(false);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : '追加章节失败';
      setError(message);
      onMessage?.(message);
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  return (
    <>
      <button
        className={compact ? 'append-chapters-trigger compact' : 'append-chapters-trigger'}
        type="button"
        disabled={disabled || !projectId || !inspirationId || maxAppend < 1}
        title={maxAppend < 1 ? `已达到系统最大章节数（${maxChapters}章）` : '基于现有剧情智能追加章节'}
        onClick={() => { setError(''); setOpen(true); }}
      >
        <Plus size={16} />追加章节
      </button>
      {open && (
        <div className="append-chapters-backdrop" role="presentation" onMouseDown={() => !busy && setOpen(false)}>
          <section className="append-chapters-dialog" role="dialog" aria-modal="true" aria-label="追加章节" onMouseDown={(event) => event.stopPropagation()}>
            <div className="append-chapters-head">
              <span><Sparkles size={19} /></span>
              <div><strong>智能追加章节</strong><p>AI会先理解已有剧情、人物、伏笔和正文，再续接新的章节计划。</p></div>
              <button type="button" disabled={busy} onClick={() => setOpen(false)} aria-label="关闭"><X size={18} /></button>
            </div>
            <div className="append-chapters-summary">
              <span>现有 <strong>{currentCount}</strong> 章</span><i />
              <span>本次最多追加 <strong>{maxAppend}</strong> 章</span><i />
              <span>系统总上限 <strong>{maxChapters}</strong> 章</span>
            </div>
            <label className="append-chapters-count">
              <span>追加数量</span>
              <input type="number" min={1} max={maxAppend} step={1} value={count} disabled={busy} onChange={(event) => setCount(Number(event.target.value))} />
              <em>章</em>
            </label>
            <p className="append-chapters-tip">追加后将同步生成章纲、剧情目标、章节梗概、章末钩子和伏笔计划，并在写作页创建对应章节。</p>
            {!countValid && <div className="append-chapters-error">请输入 1–{maxAppend} 之间的整数。</div>}
            {progress && <div className="append-chapters-progress"><Loader2 size={16} />{progress}</div>}
            {error && <div className="append-chapters-error">{error}</div>}
            <div className="append-chapters-actions">
              <button type="button" disabled={busy} onClick={() => setOpen(false)}>取消</button>
              <button className="primary" type="button" disabled={busy || !countValid} onClick={append}>
                {busy ? <Loader2 size={16} /> : <Sparkles size={16} />}{busy ? 'AI规划中' : `确认追加 ${safeCount} 章`}
              </button>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default AppendChaptersButton;
